import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, Calendar, User, Building2, FileText } from "lucide-react";
import { format } from "date-fns";

const testTypeColors = {
  complete_blood_count: "bg-red-100 text-red-700 border-red-200",
  basic_metabolic_panel: "bg-blue-100 text-blue-700 border-blue-200",
  lipid_panel: "bg-purple-100 text-purple-700 border-purple-200",
  liver_function: "bg-green-100 text-green-700 border-green-200",
  thyroid_function: "bg-orange-100 text-orange-700 border-orange-200",
  other: "bg-gray-100 text-gray-700 border-gray-200",
};

const testTypeLabels = {
  complete_blood_count: "Complete Blood Count",
  basic_metabolic_panel: "Basic Metabolic Panel",
  lipid_panel: "Lipid Panel",
  liver_function: "Liver Function",
  thyroid_function: "Thyroid Function",
  other: "Other",
};

export default function PatientTestsView({ tests, isLoading }) {
  if (isLoading) {
    return (
      <div className="grid gap-6">
        {Array(3).fill(0).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-6 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!tests || tests.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Blood Tests</h3>
        <p className="text-gray-500 mb-6">You don't have any blood test results yet.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      {tests.map((test) => (
        <Card key={test.id} className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Heart className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    {testTypeLabels[test.test_type] || test.test_type}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Test #{test.id?.slice(-8) || "N/A"}
                  </p>
                </div>
              </div>
              <Badge className={testTypeColors[test.test_type] || testTypeColors.other}>
                {testTypeLabels[test.test_type] || test.test_type}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-3">
                {test.test_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Test Date:</span>
                    <span className="font-medium">
                      {format(new Date(test.test_date), "MMM d, yyyy")}
                    </span>
                  </div>
                )}
                
                {test.doctor_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Doctor:</span>
                    <span className="font-medium">{test.doctor_name}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {test.lab_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Lab:</span>
                    <span className="font-medium">{test.lab_name}</span>
                  </div>
                )}

                {test.created_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Uploaded:</span>
                    <span className="font-medium">
                      {format(new Date(test.created_at), "MMM d, yyyy")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Results */}
            {test.results && Object.keys(test.results).length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Test Results
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(test.results).map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <span className="text-gray-600 capitalize">
                        {key.replace(/_/g, " ")}:
                      </span>
                      <div className="font-medium text-gray-900">
                        {typeof value === 'number' ? value.toFixed(1) : value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {test.notes && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-1">Notes:</p>
                <p className="text-sm text-blue-700">{test.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}